import { useEffect, useMemo, useState } from "react";
import Button from "../../../utils/component/button/button.index";
import { getAnonListConfig, getWebEnvConfig } from "../../../scripts/helpers";
import { planResourceType } from "../../../interfaces/general";
import { advancedResponseValidator, get, post, responseValidator } from "../../../scripts/api";
import API from "../../../urls/api";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "mobin-toast";
import { toastMessages } from "../../../scripts/toastMessages";
import PlanConfirmCard from "./PlanConfirmCard.index";
import EwanoCard from "./EwanoCard.index";
import PATH from "../../../urls/path";
import VoucherPlan from "./VoucherPlan";

export enum PaymentStatusEnum {
    NORMAL,
    WALLET
}

export enum WalletStatusEnum {
    INACTIVE,
    ACTIVE,
    OFF,
    LOADING
}

export enum VoucherStatusEnum {
    PERCENT = "PERCENT",
    AMOUNT = "CONSTANT"
}

export type WalletType = {
    status: WalletStatusEnum;
    balance?: number;
};

export type VoucherType = {
    status: VoucherStatusEnum;
    amount: number;
    code: string;
};

export type PaymentInformationType = {
    status: PaymentStatusEnum;
    wallet: WalletType;
    voucher?: VoucherType;
};

export const ConfirmPlan = () => {
    const navigate = useNavigate();
    const ewanoFeatureEnabled = true;
    const [paymentInformation, setPaymentInformation] = useState<PaymentInformationType>({
        status: PaymentStatusEnum.NORMAL,
        wallet: {
            status: WalletStatusEnum.LOADING
        }
    });
    const [resource, setResource] = useState<planResourceType | undefined>();

    const [voucher, setVoucher] = useState("");

    const params = useParams();
    const taxPercent = +getAnonListConfig("TAX_PERCENTAGE") * 100;
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (params.id) {
            get(
                (data, status) => {
                    if (advancedResponseValidator(data, status)) {
                        setResource(data);
                    }
                },
                API.plan.getPlan("v4", params.id)
            );
        }
    }, []);

    const planShopHandler = () => {
        return new Promise((resolve) => {
            post(
                (data, status) => {
                    if (responseValidator(status)) {
                        window.location.href = data.next_page;
                    } else {
                        toast({ title: !!toastMessages(39) ? toastMessages(39) : data.detail, type: "error" });
                    }
                    resolve(true);
                },
                API.plan.buy(),
                {
                    plan_id: params.id,
                    next_page: `${getWebEnvConfig("AbreHamrahiBaseURL")}/result-pay`,
                    new_voucher_code: paymentInformation.voucher ? paymentInformation.voucher.code : undefined
                }
            );
        });
    };

    const handleBuyWithEwano = () => {
        return new Promise((resolve) => {
            post(
                (data, status) => {
                    resolve(true);
                    if (advancedResponseValidator(data, status)) {
                        toast({ title: "بسته ابرهمراهی برای شما فعال شد.", type: "success" });
                        navigate(PATH.dashboard, { replace: true });
                    }
                },
                API.ewanoWallet.buy_plan(),
                { plan_id: params.id, new_voucher_code: paymentInformation.voucher ? paymentInformation.voucher.code : undefined }
            );
        });
    };

    const handleIncreaseEwanoBalance = () => {
        if (paymentInformation.wallet.balance !== undefined) {
            const amount = calculateFinalPrice * 10;
            setLoading(true);
            return new Promise((resolve) => {
                post(
                    (data, status) => {
                        resolve(true);
                        setLoading(false);
                        if (responseValidator(status)) {
                            window.location.href = data.redirect_url;
                        } else {
                            toast({ title: "مشکلی در انتقال به درگاه بانکی پیش آمده.", type: "error" });
                            setLoading(false);
                        }
                    },
                    API.ewanoWallet.increase_balance(),
                    {
                        amount,
                        callback_url: paymentInformation.voucher
                            ? `/drive/ewano-confirm/${params.id}/${amount}?voucher=${paymentInformation.voucher.code}`
                            : `/drive/ewano-confirm/${params.id}/${amount}`
                    }
                );
            });
        }
    };

    const handleBuyButton = () => {
        paymentInformation.status === PaymentStatusEnum.NORMAL
            ? planShopHandler()
            : paymentInformation.wallet.status === WalletStatusEnum.ACTIVE && paymentInformation.wallet.balance !== undefined
              ? paymentInformation.wallet.balance > calculateFinalPrice
                  ? handleBuyWithEwano()
                  : handleIncreaseEwanoBalance()
              : console.log("null");
    };

    const getPrice = () => {
        if (resource) {
            return (resource.price * (1 - resource.discount)) / 10;
        }
    };

    const calculateFinalPrice = useMemo(() => {
        if (resource) {
            let planPrice = (resource.price * (1 - resource.discount)) / 10;
            planPrice = planPrice + (planPrice * taxPercent) / 100;
            if (paymentInformation.voucher) {
                if (paymentInformation.voucher.status === VoucherStatusEnum.AMOUNT) {
                    planPrice = planPrice - paymentInformation.voucher.amount;
                } else {
                    // if voucher type is percent
                    planPrice = planPrice - (planPrice * paymentInformation.voucher.amount) / 100;
                }
            }
            if (paymentInformation.status === PaymentStatusEnum.WALLET && paymentInformation.wallet.balance !== undefined) {
                planPrice = planPrice - paymentInformation.wallet.balance;
            }
            return planPrice;
        } else {
            return 0;
        }
    }, [paymentInformation]);

    return (
        <div className="h-screen mx-auto px-0">
            <div className="w-full h-full overflow-hidden flex flex-col">
                <div className="overflow-auto h-full sm:rounded-[10px] w-full">
                    <div className="w-full h-full flex lg:justify-center items-center p-4">
                        {resource ? (
                            <div className={`grid grid-cols-1 w-full lg:w-auto ${ewanoFeatureEnabled ? "gap-6" : ""}`}>
                                <PlanConfirmCard
                                    resource={resource}
                                    price={getPrice()!}
                                    taxPercent={taxPercent}
                                    paymentInformation={paymentInformation}
                                    finalPrice={calculateFinalPrice}
                                />
                                {ewanoFeatureEnabled && (
                                    <EwanoCard
                                        paymentInformation={paymentInformation}
                                        setPaymentInformation={setPaymentInformation}
                                        planId={params.id!}
                                    />
                                )}

                                <VoucherPlan
                                    plan_id={params.id!}
                                    voucher={voucher}
                                    setVoucher={setVoucher}
                                    onSuccessVoucer={(voucher) => setPaymentInformation({ ...paymentInformation, voucher })}
                                    activeVoucher={paymentInformation.voucher}
                                />

                                <div className={`lg:col-span-2 ${ewanoFeatureEnabled ? "" : "mt-4"}`}>
                                    <Button
                                        disable={loading}
                                        text={
                                            paymentInformation.status === PaymentStatusEnum.NORMAL
                                                ? "پرداخت آنلاین"
                                                : paymentInformation.wallet.status === WalletStatusEnum.ACTIVE &&
                                                    paymentInformation.wallet.balance !== undefined
                                                  ? paymentInformation.wallet.balance > calculateFinalPrice
                                                      ? "پرداخت با کیف پول اوانو"
                                                      : `افزایش موجودی کیف پول (${calculateFinalPrice.toLocaleString()} تومان)`
                                                  : "ادامه"
                                        }
                                        className={`w-full border-primary-600 md:text-base text-xs md:font-bold border-[1.5px]`}
                                        onClick={handleBuyButton}
                                        autoFocus={true}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="w-full flex-1 rounded-[10px] overflow-hidden shadow-sm relative transition-all border border-transparent skeleton" />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
